/* eslint-disable radix */
import { CancelToken } from 'axios';
import api from '../../../api';
import { getDialerSessionUniqueId } from './helper';
import { ResponseSuccess } from '../../../shared/types';
import {
  AddPhoneNumbersToCartPayload,
  RemovePhoneNumberFromCartPayload,
  SearchPhoneNumbersPayload,
  PurchasePhoneNumbersCartPayload,
  GetPhoneNumbersListPayload,
  PhoneNumberListingFilters,
} from '../types/phone-numbers';
import {
  GetContactsListForManualCallDialingPayload,
  GetDialerPhoneNumberListPayload,
  InitiateCallPayload,
  EndInitiatedCallPayload,
  PickupCallPayload,
  AttachProspectToCallPayload,
} from '../types/manual-call-dialing';
import {
  getVoicemailTemplateMinimalListProps,
  dropVoicemailTemplatePayload,
  callRecordingPayload,
  listAllCallLogsPayload,
  GetCallOutcomeListProps,
  GetVoicemailTemplateListProps,
  UpdateVoicemailTemplateProps,
  CreateVoicemailTemplateProps,
  ReportsPayload,
  prospectCallLogsDrawerHeaderPayload,
  createCallLogsNotePayload,
  updateCallLogsNotePayload,
} from '../types';
// Phone Numbers
export const searchRegions = async (params: {
  searchText?: string;
  dialerCountryId?: string;
  pageNumber?: number;
  pageSize?: number;
}) =>
  api.get<ResponseSuccess>('/dialer/countries/localities/search', { params });

export const getCountriesOptionsForNumbersSearch = async () =>
  api.get<ResponseSuccess>('/dialer/countries');

export const searchPhoneNumbers = async (payload: SearchPhoneNumbersPayload) =>
  api.post<ResponseSuccess>('/dialer/phone-numbers/search', payload);

export const addPhoneNumbersToCart = async (
  payload: AddPhoneNumbersToCartPayload,
) => api.post<ResponseSuccess>('/dialer/phone-numbers/add-to-cart', payload);

export const removePhoneNumberFromCart = async (
  payload: RemovePhoneNumberFromCartPayload,
) =>
  api.patch<ResponseSuccess>('/dialer/phone-numbers/remove-from-cart', payload);

export const getPhoneNumbersCart = async () =>
  api.get<ResponseSuccess>('/dialer/phone-numbers/get-cart');

export const purchasePhoneNumbersCart = async (
  payload: PurchasePhoneNumbersCartPayload,
) => api.post<ResponseSuccess>('/dialer/phone-numbers/cart/purchase', payload);

// ** Manual Call Dialing APIs
/**
 * Get Contacts List for Manual Call Dialing
 * @param payload - GetContactsListForManualCallDialingPayload -> { pageNum: number, pageSize: number, search?: string }
 * @param cancelToken - Optional CancelToken for request cancellation
 * @returns Contacts List
 */
export const getContactsListForManualCallDialing = async (
  payload: GetContactsListForManualCallDialingPayload,
  cancelToken?: CancelToken,
) =>
  api.post<ResponseSuccess>(
    '/contacts/dialer/list',
    { ...payload },
    { cancelToken },
  );

/**
 * Create Twillio Voice Token to initiate a call
 * @returns Twillio Voice Token
 */
export const createTwillioVoiceToken = async () =>
  api.post<ResponseSuccess>('/dialer/call/voice-token', {
    uniqueId: getDialerSessionUniqueId(),
  });

/**
 * Initiate a call
 * @param payload - InitiateCallPayload -> { fromNumber: string, toNumber: string }
 * @returns Call Initiation Response -> { callId: string }
 */
export const initiateCall = async (payload: InitiateCallPayload) =>
  api.post<ResponseSuccess>('/dialer/call/start', {
    ...payload,
    uniqueId: getDialerSessionUniqueId(),
  });

/**
 * End an initiated call
 * @param callId - Call ID
 * @returns Call Ending Response -> {message : string}
 */
export const endInitiatedCall = async (payload: EndInitiatedCallPayload) =>
  api.post<ResponseSuccess>(`/dialer/call/end`, { ...payload });

/**
 * Record browser session that picked up a call
 * @param payload - PickupCallPayload -> { callId: number }
 * @returns { message: string }
 */
export const pickupCall = async (payload: PickupCallPayload) =>
  api.post<ResponseSuccess>('/dialer/call/pickup', {
    ...payload,
    uniqueId: getDialerSessionUniqueId(),
  });

/**
 * Get minimal phone number list for dialer (Call Via dropdown)
 * @param payload - GetDialerPhoneNumberListPayload -> { prospectPhoneNumber?: string }
 * @returns List of phone numbers assigned to current user
 */
export const getDialerPhoneNumberList = async (
  payload: GetDialerPhoneNumberListPayload,
) => api.post<ResponseSuccess>('/dialer/phone-numbers/dialer-list', payload);

export const getMyPhoneNumberList = async (
  payload: GetPhoneNumbersListPayload,
) => api.post<ResponseSuccess>('/dialer/phone-numbers', payload);

export const getCountryCodeOptionsListForFilters = async () =>
  api.get<ResponseSuccess>('/dialer/phone-numbers/country-code-list');

export const getRegionOptionsListForFilters = async () =>
  api.get<ResponseSuccess>('/dialer/phone-numbers/region-list');

export const getPhoneNumberLabels = async (params: { search?: string }) =>
  api.get<ResponseSuccess>('/dialer/phone-numbers/labels', { params });

export const searchMemberPhoneNumbers = async (params: {
  search?: string;
  pageNumber?: number;
  pageSize?: number;
}) => api.get<ResponseSuccess>('/dialer/member-phone-numbers', { params });

export const updatePhoneNumberSettings = async (payload: {
  phoneNumberIds: number[];
  settings: Array<{ code: string; value: string | null }>;
}) => api.post<ResponseSuccess>('/dialer/phone-numbers/settings', payload);

export const getPhoneNumberDetails = async (id: number) =>
  api.get<ResponseSuccess>(`/dialer/phone-numbers/${id}/details`);

export const assignPhoneNumber = async (payload: {
  phoneNumberIds: number[];
  userId: number;
}) => api.post<ResponseSuccess>('/dialer/phone-numbers/assign', payload);

export const bulkAssignPhoneNumber = async (payload: {
  deselectedPhoneNumberIds?: number[];
  phoneNumberFilters?: PhoneNumberListingFilters;
  userId: number;
}) => api.post<ResponseSuccess>('/dialer/phone-numbers/bulk-assign', payload);

export const setPhoneNumberLabel = async (payload: {
  phoneNumberIds: number[];
  label: string;
}) => api.post<ResponseSuccess>('/dialer/phone-numbers/label', payload);

export const bulkSetPhoneNumberLabel = async (payload: {
  deselectedPhoneNumberIds?: number[];
  phoneNumberFilters?: PhoneNumberListingFilters;
  label: string;
}) => api.post<ResponseSuccess>('/dialer/phone-numbers/bulk-label', payload);

// Bulk Update Phone Number Settings
export const bulkUpdatePhoneNumberSettings = async (payload: {
  deselectedPhoneNumberIds?: number[];
  phoneNumberFilters?: PhoneNumberListingFilters;
  settings: Array<{ code: string; value: string | null }>;
}) => api.post<ResponseSuccess>('/dialer/phone-numbers/bulk-settings', payload);

// Set primary phone number for authenticated user
export const setPrimaryPhoneNumber = async (phoneNumberId: number) =>
  api.put<ResponseSuccess>(`/dialer/phone-numbers/${phoneNumberId}/primary`);

// Delete phone number by ID
export const deletePhoneNumber = async (id: number) =>
  api.delete<ResponseSuccess>(`/dialer/phone-numbers/${id}`);

// Revoke phone number release
export const revokePhoneNumberRelease = async (id: number) =>
  api.put<ResponseSuccess>(`/dialer/phone-numbers/${id}/revoke-release`);

// Get pre-signed URL for document upload
export const getPreSignedUrl = async (payload: {
  fileName: string;
  documentType: string;
  fileType: string;
  fileSize: number;
  metadata?: Record<string, any>;
}) => api.post<ResponseSuccess>('/user/document/pre-signed-url', payload);

// Get voicemail template minimal list
export const getVoicemailTemplateMinimalList = async (
  params: getVoicemailTemplateMinimalListProps,
) =>
  api.get<ResponseSuccess>('/dialer/voicemail-template/minimal-list', {
    params,
  });

// Drop voicemail template
export const dropVoicemailTemplate = async (
  payload: dropVoicemailTemplatePayload,
) => api.post<ResponseSuccess>('/dialer/voicemail-template/drop', payload);

// Call recording
export const callRecording = async (payload: callRecordingPayload) =>
  api.post<ResponseSuccess>('/dialer/call/recording', payload);

// List all call logs
export const listAllCallLogs = async (payload: listAllCallLogsPayload) =>
  api.post<ResponseSuccess>('/dialer/calls/list-all', payload);

// List missed calls
export const listMissedCalls = async (payload: listAllCallLogsPayload) =>
  api.post<ResponseSuccess>('/dialer/calls/list-missed', payload);

// List incoming voicemails
export const listIncomingVoicemails = async (payload: listAllCallLogsPayload) =>
  api.post<ResponseSuccess>('/dialer/calls/list-incoming-voicemail', payload);
/**
 * Get network diagnostic token
 * @returns Network Diagnostic Token
 */
export const getNetworkDiagnosticToken = async () =>
  api.get<ResponseSuccess>('/dialer/diagnostic-token');

// Delete call log
export const deleteCallLog = async (callId: number) =>
  api.delete<ResponseSuccess>(`/dialer/calls/${callId}`);

// Mark call as seen
export const markUnseenMissedCallCount = async () =>
  api.get<ResponseSuccess>('/dialer/calls/unseen-missed-count');

// Mark call as seen
export const markCallAsSeen = async (callIds?: number[]) =>
  api.patch<ResponseSuccess>(`/dialer/calls/mark-as-seen`, { callIds });

// Get signed URL for call recording
export const getSignedUrlForCallRecording = async (callId: number) =>
  api.post<ResponseSuccess>(`/dialer/calls/recording/signed-url`, { callId });

// Get call outcome list
export const getCallOutcomeList = async (params: GetCallOutcomeListProps) =>
  api.get<ResponseSuccess>('/dialer/call-outcome/list', { params });

// Get voicemail template list
export const getVoicemailTemplateList = async (
  params: GetVoicemailTemplateListProps,
) => api.get<ResponseSuccess>('/dialer/voicemail-templates', { params });

// Get signed URL for voicemail template
export const getSignedUrlForVoicemailTemplate = async (templateId: number) =>
  api.post<ResponseSuccess>('/dialer/voicemail-templates/signed-url', {
    templateId,
  });

// Create voicemail template
export const createVoicemailTemplate = async (
  payload: CreateVoicemailTemplateProps,
) => api.post<ResponseSuccess>(`/dialer/voicemail-templates`, payload);

// Update voicemail template
export const updateVoicemailTemplate = async (
  id: number,
  payload: UpdateVoicemailTemplateProps,
) => api.put<ResponseSuccess>(`/dialer/voicemail-templates/${id}`, payload);

// Delete voicemail template
export const deleteVoicemailTemplate = async (id: number) =>
  api.delete<ResponseSuccess>(`/dialer/voicemail-templates/${id}`);

export const callLogsListCounts = async (payload?: listAllCallLogsPayload) =>
  api.post<ResponseSuccess>('/dialer/calls/list-count', payload);

// Assign call outcome
export const assignCallOutcome = async (payload: {
  outcomeId: number;
  dialerCallLogId: number;
}) => api.post<ResponseSuccess>('/dialer/call-outcome/assign', payload);

// Reports stats
export const getReportsStats = async (payload: ReportsPayload) =>
  api.post<ResponseSuccess>('/dialer/reports/stats', payload);

// Report Individual List
export const getReportIndividualList = async (payload: ReportsPayload) =>
  api.post<ResponseSuccess>('/dialer/reports/individual-list', payload);

// Report Teamwise List
export const getReportTeamwiseList = async (payload: ReportsPayload) =>
  api.post<ResponseSuccess>('/dialer/reports/team-list', payload);

// Export call reports - table summary (csv)
export const exportCallReports = async (payload: ReportsPayload) =>
  api.post<ResponseSuccess>('/dialer/reports/export/call-summary', payload);

// Export call logs detailed breakdown (csv)
export const exportCallLogsDetailed = async (payload: {
  userIds: number[];
  startDate: string;
  endDate: string;
}) =>
  api.post<ResponseSuccess>('/dialer/reports/export/call-detailed', payload);

// Get call logs prospect drawer header
export const getCallLogsProspectDrawerHeader = async (
  payload: prospectCallLogsDrawerHeaderPayload,
) => api.post<ResponseSuccess>(`/dialer/prospect/calls`, payload);

// Get call logs prospect drawer header
export const getCallLogsProspectProfileSection = async (callId: number) =>
  api.get<ResponseSuccess>(`/dialer/call/${callId}/details`);

// Get call logs prospect drawer header
export const createCallLogsNote = async (payload: createCallLogsNotePayload) =>
  api.post<ResponseSuccess>(`/dialer/calls/notes`, payload);

export const updateCallLogsNote = async ({
  noteId,
  callId: _callId,
  ...rest
}: updateCallLogsNotePayload) =>
  api.patch<ResponseSuccess>(`/dialer/calls/notes/${noteId}`, rest);

// Get phone call mentionable users
export const getPhoneCallMentionableUsers = async (callId: number) =>
  api.get<ResponseSuccess>(`/user/${callId}/phone-call-mentionable-users`);

// Attach prospect to call
export const attachProspectToCall = async (
  payload: AttachProspectToCallPayload,
) => api.post<ResponseSuccess>('/contacts/attach-to-call', payload);
